import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { batchMoveHoldings, moveHoldingV1, moveV2Holding } from '../../composables/fetchAPI';
import { usePortfolio } from '../../context/PortfolioContext';
interface MoveHoldingModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  sourceSubPort: any;      // พอร์ตย่อยต้นทาง
  allSubPorts: any[];      // รายชื่อพอร์ตย่อยทั้งหมด (เพื่อเอามาทำ Dropdown)
  stocks: any[];      // หุ้นที่ถูกกดเลือกมา
  onSave: () => void;      // สั่ง Refresh หลังย้ายเสร็จ
}

const MoveHoldingModal: React.FC<MoveHoldingModalProps> = ({
  isOpen,
  onClose,
  portfolioId,
  sourceSubPort,
  allSubPorts,
  stocks,
  onSave
}) => {
  const [targetSubPortId, setTargetSubPortId] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { displayMode } = usePortfolio(); // ดึง displayMode จาก context
  // คัดกรองพอร์ตย่อยปลายทาง (ตัดพอร์ตย่อยต้นทางออกไป)
  const availableTargetPorts = allSubPorts.filter(sub => sub.subPortId !== sourceSubPort?.subPortId);
  const getStockDetails = (symbol: string) => {
    return sourceSubPort?.stocks?.find((s: any) => s.stockSymbol === symbol) || { stockSymbol: symbol, quantity: 0, avgBuyPrice: 0 };
  };
  const stocksToMove = stocks.map(symbol => getStockDetails(symbol));

  // Reset ฟอร์มเมื่อเปิด Modal ใหม่
  useEffect(() => {
    if (isOpen) {
      setTargetSubPortId('');
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setNote('');
    }
  }, [isOpen]);

  if (!isOpen || !stocks) return null;

  const symbol = stocks[0]?.assetSymbol || stocks[0]?.stockSymbol || stocks[0]?.symbol;
  const quantity = stocks[0]?.quantity || 0;
  const avgPrice = stocks[0]?.avgEntryPrice || stocks[0]?.avgBuyPrice || 0;
  const stockList = Array.isArray(stocks[0]) ? stocks[0] : stocks;


  const handleMove = async () => {
    if (!targetSubPortId) {
      toast.error('กรุณาเลือกพอร์ตปลายทาง');
      return;
    }

    setIsSubmitting(true);
    try {

      const finalNote = note.trim() === '' ? 'ย้ายรายการหุ้น' : note.trim();

      if (displayMode === 'V1') {
        for (const stock of stockList) {
          const symbol = stock.assetSymbol || stock.stockSymbol || stock.symbol;

          // ส่ง targetSubPortId เป็น null ได้ถ้าปลายทางคือ Root
          await moveHoldingV1(
            portfolioId,
            sourceSubPort?.subPortId || null,
            symbol,
            targetSubPortId === 'ROOT' ? null : targetSubPortId
          );
        }

      }

      if (displayMode === 'V2') {
        if (stockList.length === 1) {

          // 1. ย้ายเดี่ยว
          await moveV2Holding(portfolioId, {
            fromSubPortId: sourceSubPort.subPortId,
            toSubPortId: targetSubPortId,
            assetSymbol: stockList[0].assetSymbol || stockList[0].stockSymbol || stockList[0].symbol,
            moveMode: 'ALL',
            quantity: stockList[0].quantity,
            note: finalNote
          });
        } else {
          // 2. ย้ายกลุ่ม (Batch)
          const payload = {
            note: finalNote,
            transactionDate: new Date(transactionDate).toISOString(),
            items: stockList.map(s => ({
              fromSubPortId: sourceSubPort.subPortId,
              toSubPortId: targetSubPortId,
              assetSymbol: s.assetSymbol || s.stockSymbol || s.symbol,
              moveMode: 'ALL' as 'ALL'
            }))
          };
          await batchMoveHoldings(portfolioId, payload);
        }
      }

      toast.success('ย้ายหุ้นเรียบร้อย');
      onSave();
      onClose();
    } catch (error: any) {
      console.error('Move error details:', error);

      // ✅ ปรับส่วนนี้เพื่อดึงข้อความจาก API โดยตรง
      const errorMessage =
        error.response?.data?.message || // สำหรับ Axios
        error.message ||                 // สำหรับ Fetch ปกติ
        'เกิดข้อผิดพลาดในการย้ายหุ้น';
      if (error.message === 'Stock already exists in target') {
        toast.error('หุ้นตัวนี้มีอยู่ในพอร์ตปลายทางแล้ว กรุณาจัดการหุ้นเดิมก่อนย้าย');
      } else {
        toast.error(errorMessage);
      }

    } finally {
      setIsSubmitting(false);
    }
  };
  const isBulk = stocks.length > 1;

  // ถ้ามีหุ้นหลายตัว ให้เอา Symbol มาต่อกันด้วย comma
  const headerSymbol = isBulk
    ? stocks.map(s => (s.assetSymbol || s.stockSymbol || s.symbol)).join(', ')
    : (stocks[0]?.assetSymbol || stocks[0]?.stockSymbol || stocks[0]?.symbol);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm transition-opacity"
      // onClick={onClose}
    >
      <div
        className="rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.5)] w-full max-w-md border flex flex-col"
        style={{ backgroundColor: 'var(--component-bg-color)', borderColor: 'var(--border-item-light)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* --- Header --- */}
        <div className="flex justify-between items-center p-5 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>
            ย้าย <span style={{ color: 'var(--yellow-text-color)' }}>{headerSymbol}</span> ไปยัง พอร์ตย่อยอื่น
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors font-bold text-xl">
            &#x2715;
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* --- Section: From --- */}
          <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--bg-item)', borderColor: 'var(--border-item-light)' }}>
            <p className="text-xs font-bold mb-3" style={{ color: 'var(--secondary-text-color)' }}>
              {/* ใช้ stockList.length ให้ตรงกับจำนวนหุ้นจริงๆ */}
              จากพอร์ตย่อย ({sourceSubPort?.name}) - {stockList.length} รายการ
            </p>

            <div className="text-sm text-gray-300">
              {stockList.length === 1 ? (
                // เคส: มีหุ้น 1 ตัว
                <div className="flex justify-between items-center text-sm">
                  <span className="font-bold">ย้ายหุ้น: </span>
                  <span className="font-bold text-yellow-400">
                    {stockList[0].assetSymbol || stockList[0].stockSymbol || stockList[0].symbol}
                  </span>
                  <span className="text-gray-300">
                    {stockList[0].quantity?.toLocaleString(undefined, { maximumFractionDigits: 4 })} หุ้น
                  </span>
                </div>
              ) : (
                // เคส: มีหุ้นหลายตัว
                <div className="space-y-1">
                  <div className="text-sm text-gray-300">
                    <span className="font-bold">ย้ายหุ้น: </span>
                    {/* ✅ เปลี่ยนจาก stocks.map เป็น stockList.map */}
                    {stockList
                      .map(s => s.assetSymbol || s.stockSymbol || s.symbol)
                      .join(' , ')}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* --- Section: Target Segment --- */}
          <div>
            <label className="block text-xs font-bold mb-2" style={{ color: 'var(--secondary-text-color)' }}>พอร์ตย่อยเป้าหมาย</label>
            <select
              value={targetSubPortId}
              onChange={(e) => setTargetSubPortId(e.target.value)}
              className="w-full p-3 rounded-lg border focus:outline-none focus:ring-2 font-bold cursor-pointer transition-colors"
              style={{
                backgroundColor: 'var(--bg-item)',
                color: 'var(--text-color)',
                borderColor: 'var(--border-item-light)',
                outlineColor: 'var(--yellow-text-color)'
              }}
            >
              <option value="" disabled>-- เลือกพอร์ตย่อยเป้าหมาย --</option>
              {availableTargetPorts.map((sub: any) => (
                <option key={sub.subPortId} value={sub.subPortId}>{sub.name}</option>
              ))}
            </select>
          </div>


          {displayMode === 'V2' && (
            <div>
              <label className="block text-xs font-bold mb-2" style={{ color: 'var(--secondary-text-color)' }}>วันที่ทำรายการ</label>
              <input
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                className="w-full p-3 rounded-lg border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-item)',
                  color: 'var(--text-color)',
                  borderColor: 'var(--border-item-light)',
                  outlineColor: 'var(--yellow-text-color)'
                }}
              />
            </div>
          )}

          {displayMode === 'V2' && (
            <div>
              <label className="block text-xs font-bold mb-2" style={{ color: 'var(--secondary-text-color)' }}>หมายเหตุ</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="บันทึกช่วยจำ..."
                className="w-full p-3 rounded-lg border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-item)',
                  color: 'var(--text-color)',
                  borderColor: 'var(--border-item-light)',
                  outlineColor: 'var(--yellow-text-color)'
                }}
              />
            </div>
          )}
        </div>


        {/* --- Footer Buttons --- */}
        <div className="p-5 border-t flex justify-end gap-3" style={{ borderColor: 'var(--border-item-light)', backgroundColor: 'var(--component-bg-color)' }}>
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg font-bold transition-colors"
            style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
          >
            ยกเลิก
          </button>
          <button
            onClick={handleMove}
            disabled={!targetSubPortId || isSubmitting}
            className={`px-6 py-2 rounded-lg bg-yellow-500 text-black font-bold  shadow-md transition-transform ${!targetSubPortId || isSubmitting ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
            // style={{ color: 'var(--text-primary-dark)' }}
          >
            {isSubmitting ? 'กำลังย้าย...' : 'ย้ายไปยังพอร์ตย่อย'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default MoveHoldingModal;